// src/components/..../logspage-ai.js

import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Typography, Tag, Descriptions, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css';
// import AIAgent from './AIAgent/AIAgent'; // AI Agent hidden from the UI

const { Title, Text } = Typography;

// Helper function to format a Unix timestamp into a readable date and time
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp * 1000).toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: true
  });
};

const formatTitle = (key) => {
  const result = key.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

const LogsPageAI = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logType, username, logData } = location.state || {};

  const columns = useMemo(() => {
    if (!logData || !Array.isArray(logData) || logData.length === 0) {
      return [];
    }
    
    // Define a curated set of columns for the main table view for readability
    return [
      {
        title: 'Request ID',
        dataIndex: 'requestId',
        key: 'requestId',
        width: 250,
        render: (text) => <Text copyable={{ tooltips: ['Copy', 'Copied!'] }}>{text}</Text>,
      },
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        width: 200,
        render: (timestamp) => formatTimestamp(timestamp),
      },
      {
        title: 'Username',
        dataIndex: ['userInfo', 'username'],
        key: 'username',
        width: 200,
      },
      {
        title: 'Request Type',
        dataIndex: 'requestType',
        key: 'requestType',
        width: 150,
        render: (type) => <Tag color="blue">{type}</Tag>,
      },
      {
        title: 'Policy Action',
        dataIndex: ['policy', 'policyAction'],
        key: 'policyAction',
        width: 150,
        render: (action) => {
          const isSuccess = String(action).toLowerCase() === 'success';
          return <Tag color={isSuccess ? 'success' : 'error'}>{action}</Tag>;
        },
      },
      {
        title: 'Location',
        key: 'location',
        width: 150,
        render: (_, record) => `${record.location.city}, ${record.location.country}`,
      }
    ];
  }, [logData]);

  // This function renders the detailed view when a row is expanded
  const expandedRowRender = (record) => {
    const nestedDetails = Object.keys(record).filter(key => typeof record[key] === 'object' && record[key] !== null);

    return (
      <div style={{ padding: '16px', backgroundColor: '#f9f9f9' }}>
        {nestedDetails.map(key => (
          <Descriptions
            key={key}
            title={<Title level={5} style={{ textTransform: 'capitalize' }}>{formatTitle(key)} Details</Title>}
            bordered
            column={1}
            size="small"
            style={{ marginBottom: '24px' }}
          >
            {Object.entries(record[key]).map(([subKey, subValue]) => (
              <Descriptions.Item key={subKey} label={formatTitle(subKey)}>
                {String(subValue)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        ))}
      </div>
    );
  };
  
  if (!logData || !Array.isArray(logData) || logData.length === 0) {
    return (
      <div style={{ padding: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={2} style={{ margin: 0 }}>Log Viewer</Title>
           <Button 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </div>
        <Text>No log data was provided or the logs are empty. Please use the Policy Co-Pilot to fetch logs.</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={2} style={{ margin: 0 }}>Policy Co-Pilot Log Viewer</Title>
        <Button 
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Text strong>Displaying logs for: </Text><Text>{username}</Text><br/>
        <Text strong>Log Type: </Text><Text>{logType}</Text>
      </div>
      <Table
        columns={columns}
        dataSource={logData.map(log => ({ ...log, key: log.requestId }))}
        bordered
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10 }}
        expandable={{
          expandedRowRender: expandedRowRender,
          rowExpandable: record => true,
        }}
      />
    </div>
  );
};

export default LogsPageAI;